import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { baseURL, baseURLnoapi } from '../Constants';
import '../styles/components/match-page-overview.scss';
import { FaCalendarAlt, FaClock, FaTwitch, FaFilter, FaSearch, FaCheckCircle, FaPlay } from 'react-icons/fa';
import { liveMatchService } from '../services/liveMatchService';
import { getMatchFormat as getMatchFormatUtil } from '../utils/matchUtils';

// Types
interface Team {
  teamId: string;
  teamName: string;
  teamLogo: string;
  ownerId: string | null;
  season: number;
  division: string;
  twitter: string | null;
  coachID: string | null;
  website: string | null;
  seed: string;
  playoffSeed: string;
}

interface Match {
  challonge_id: string;
  team1: Team;
  team2: Team;
  isPlayoff: boolean;
  playoffPlayed: boolean;
  division: string;
  match_time: string;
  stream_url: string | null;
  veto: string | null;
  roundNumber: number | null;
  season: number;
  hidden: boolean;
  bo3?: boolean; // Added to check if match is Best of 3
  isPlayed?: boolean; // New field to track if match is played
}

interface PlayedMatch {
  matchid: string; // Note: played matches use 'matchid', not 'challonge_id'
  mapName: string;
  season: number;
  team1score: number;
  team2score: number;
  team1won: boolean;
  // Add other played match fields as needed from getAllMatches response
  [key: string]: any;
}

interface MatchPageOverviewProps {
  searchTerm?: string;
  matchStatusFilter?: 'all' | 'played' | 'upcoming';
  sortBy?: 'date' | 'playoff' | 'alphabetical';
  season?: number;
  view?: 'upcoming' | 'results'; // New prop for toggle view
  onMatchesLoaded?: (matches: Match[], playedMatches: PlayedMatch[]) => void; // Callback to pass matches to parent
  isAdmin?: boolean; // Admin status for showing hidden matches
}

const MatchPageOverview: React.FC<MatchPageOverviewProps> = ({
  searchTerm = '',
  matchStatusFilter = 'all',
  sortBy = 'date',
  season,
  view = 'upcoming',
  isAdmin = false,
  onMatchesLoaded
}) => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [filteredMatches, setFilteredMatches] = useState<Match[]>([]);
  const [playedMatches, setPlayedMatches] = useState<PlayedMatch[]>([]);
  const [liveMatchIds, setLiveMatchIds] = useState<string[]>([]); // Track live match challonge IDs
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states (some now come from props)
  const [seasons, setSeasons] = useState<number[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number | null>(season || null);
  const [searchQuery, setSearchQuery] = useState<string>(''); // Keep for fallback

  // Cache for season 1 team logos (teamId -> logoUrl)
  const [season1TeamLogos, setSeason1TeamLogos] = useState<Record<string, string>>({});

  // Team placeholder image
  const teamPlaceholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

  // Helper function to convert short team IDs (like "69") to UUID format
  const shortIdToUuid = (shortId: string): string => {
    // If already a UUID, return as-is
    if (shortId.includes('-')) {
      return shortId;
    }
    // Convert short ID to UUID format: 00000000-0000-0000-0000-000000000069
    const paddedId = shortId.padStart(12, '0');
    return `00000000-0000-0000-0000-${paddedId}`;
  };

  // Helper function to get team logo URL with fallback
  const getTeamLogoUrl = (team: Team) => {
    // For season 1, check cache first (fetched from backend with correct logo URLs)
    if (team.season === 1 && team.teamId) {
      // Try direct lookup first
      if (season1TeamLogos[team.teamId]) {
        return season1TeamLogos[team.teamId];
      }
      // Try converting short ID to UUID format
      const uuidId = shortIdToUuid(team.teamId);
      if (season1TeamLogos[uuidId]) {
        return season1TeamLogos[uuidId];
      }
      // Fallback to placeholder if not in cache yet
      return teamPlaceholderImage;
    }
    // For other seasons, try the direct logo URL first if it exists
    if (team.teamLogo && team.teamLogo.startsWith('http')) {
      return team.teamLogo;
    } else if (team.teamLogo) {
      return `${baseURLnoapi}${team.teamLogo}`;
    }
    // Try the team ID API endpoint as fallback
    else if (team.teamId) {
      return `${baseURL}/Team/${team.teamId}/logo`;
    }
    // Final fallback to placeholder
    else {
      return teamPlaceholderImage;
    }
  };
  
  // Function to format date and time
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      hour12: true
    }).replace(':00', ''); // Shows "1 PM" instead of "01:00 PM"
  };
  
  // Function to determine if a match is today
  const isMatchToday = (dateString: string) => {
    const matchDate = new Date(dateString);
    const today = new Date();
    
    return matchDate.getDate() === today.getDate() &&
           matchDate.getMonth() === today.getMonth() &&
           matchDate.getFullYear() === today.getFullYear();
  };
  
  // Function to determine if a match is this week
  const isMatchThisWeek = (dateString: string) => {
    const matchDate = new Date(dateString);
    const today = new Date();
    const oneWeek = 7 * 24 * 60 * 60 * 1000; // One week in milliseconds
    
    return matchDate.getTime() - today.getTime() < oneWeek && 
           matchDate.getTime() >= today.getTime() &&
           !isMatchToday(dateString);
  };

  // Function to check if a match is played
  // The ONLY way to know if a match is played is if the challonge_id
  // exists as a matchid in the getAllMatches response
  const isMatchPlayed = (challongeId: string): boolean => {
    const isPlayed = playedMatches.some(playedMatch => playedMatch.matchid === challongeId);
    if (isPlayed) {
    }
    return isPlayed;
  };

  // Function to check if a match is currently live
  // Uses the actual live match tracking system via liveMatchService
  const isMatchLive = (match: Match): boolean => {
    if (isMatchPlayed(match.challonge_id)) {
      return false; // Already finished
    }

    // Check if this match's challonge_id is in the list of live matches
    return liveMatchIds.includes(match.challonge_id);
  };

  // Function to get match format (BO1 or BO3)
  // Uses shared utility with priority: bo3 field > round number heuristic > default
  const getMatchFormat = (match: Match): string => {
    return getMatchFormatUtil(match);
  };

  // Function to get match score from played matches data with proper team mapping
  const getMatchScore = (match: Match): { team1Score: number; team2Score: number; team1Won: boolean } | null => {
    const playedMatch = playedMatches.find(pm => pm.matchid === match.challonge_id);
    if (playedMatch) {
      // Check if we have team data in the played match to verify order
      // Try multiple approaches to determine team order
      
      // Method 1: Check team IDs if available
      const playedMatchTeam1Id = playedMatch.team1?.teamId || playedMatch.team1Id || null;
      const playedMatchTeam2Id = playedMatch.team2?.teamId || playedMatch.team2Id || null;
      
      if (playedMatchTeam1Id && playedMatchTeam2Id) {
        if (match.team1.teamId === playedMatchTeam1Id && match.team2.teamId === playedMatchTeam2Id) {
          // Teams are in the same order
          return {
            team1Score: playedMatch.team1score || 0,
            team2Score: playedMatch.team2score || 0,
            team1Won: playedMatch.team1won || false
          };
        } else if (match.team1.teamId === playedMatchTeam2Id && match.team2.teamId === playedMatchTeam1Id) {
          // Teams are reversed - swap the scores
          return {
            team1Score: playedMatch.team2score || 0,
            team2Score: playedMatch.team1score || 0,
            team1Won: !playedMatch.team1won || false
          };
        }
      }
      
      // Method 2: Check team names if available
      const playedMatchTeam1Name = playedMatch.team1?.teamName || playedMatch.team1Name || null;
      const playedMatchTeam2Name = playedMatch.team2?.teamName || playedMatch.team2Name || null;
      
      if (playedMatchTeam1Name && playedMatchTeam2Name) {
        if (match.team1.teamName === playedMatchTeam1Name && match.team2.teamName === playedMatchTeam2Name) {
          // Teams are in the same order
          return {
            team1Score: playedMatch.team1score || 0,
            team2Score: playedMatch.team2score || 0,
            team1Won: playedMatch.team1won || false
          };
        } else if (match.team1.teamName === playedMatchTeam2Name && match.team2.teamName === playedMatchTeam1Name) {
          // Teams are reversed - swap the scores
          return {
            team1Score: playedMatch.team2score || 0,
            team2Score: playedMatch.team1score || 0,
            team1Won: !playedMatch.team1won || false
          };
        }
      }
      
      // Fallback: If we can't verify team order, return scores as-is
      // Log this so we can debug which matches have this issue
      console.warn(`Could not verify team order for match ${match.challonge_id}. Teams: ${match.team1.teamName} vs ${match.team2.teamName}. Using default order.`);
      return {
        team1Score: playedMatch.team1score || 0,
        team2Score: playedMatch.team2score || 0,
        team1Won: playedMatch.team1won || false
      };
    }
    return null;
  };

  // Function to get available seasons
  const getAvailableSeasons = async () => {
    // Based on your current data, you only have seasons 1 and 2
    return [1, 2];
  };
  
  // Update selectedSeason when season prop changes
  useEffect(() => {
    if (season !== undefined && season !== selectedSeason) {
      setSelectedSeason(season);
    }
  }, [season]);

  // Fetch season 1 team logos when season 1 is selected
  useEffect(() => {
    const fetchSeason1TeamLogos = async () => {
      if (selectedSeason !== 1) return;

      try {
        // Fetch all teams for season 1 from the backend
        const response = await axios.get(`${baseURL}/Team?season=1`);
        if (response.data && Array.isArray(response.data)) {
          const logoMap: Record<string, string> = {};
          response.data.forEach((team: any) => {
            if (team.id && team.logo) {
              logoMap[team.id] = team.logo;
            }
          });
          setSeason1TeamLogos(logoMap);
        }
      } catch (error) {
        console.error('Failed to fetch season 1 team logos:', error);
      }
    };

    fetchSeason1TeamLogos();
  }, [selectedSeason]);

  // Fetch live match IDs from the server
  useEffect(() => {
    const fetchLiveMatches = async () => {
      try {
        // Get all currently live match IDs from backend
        const liveIds = await liveMatchService.getLiveMatchChallongeIds();
        setLiveMatchIds(liveIds);
      } catch (error) {
        // Error handling is done in the service, just set empty array
        setLiveMatchIds([]);
      }
    };

    // Fetch immediately
    fetchLiveMatches();

    // Poll every 30 seconds to keep live matches updated
    const interval = setInterval(fetchLiveMatches, 30000);

    return () => clearInterval(interval);
  }, []);
  
  // Load matches data from API
  useEffect(() => {
    const fetchMatches = async () => {
      setLoading(true);
      try {
        // First get available seasons
        const availableSeasons = await getAvailableSeasons();
        setSeasons(availableSeasons);
        
        // Set default season to the latest
        const defaultSeason = Math.max(...availableSeasons);
        if (!selectedSeason) {
          setSelectedSeason(defaultSeason);
        }
        
        const seasonToFetch = selectedSeason || defaultSeason;
        
        // API Logic:
        // - getUpcomingMatches: returns all upcoming matches (all seasons) - THIS IS OUR MAIN DATA
        // - getAllMatches?season=X: returns all PLAYED matches for that season - ONLY FOR STATUS CHECKING
        // - Match by: upcomingMatch.challonge_id === playedMatch.matchid
        // - IGNORE playoffPlayed field entirely
        
        let allMatches: Match[] = [];
        let playedMatchIds: string[] = [];
        
        // Fetch upcoming matches (all upcoming matches regardless of season)
        const upcomingMatchesResponse = await axios.get(
          `https://api.rising.powerstats.dk/RisingStats/api/Match/getUpcomingMatches`
        );
        
        // Fetch played matches for the selected season
        let fetchedPlayedMatches: PlayedMatch[] = [];
        try {
          const playedMatchesResponse = await axios.get(
            `https://api.rising.powerstats.dk/RisingStats/api/Match/getAllMatches?season=${seasonToFetch}`
          );


          // Process played matches (used only for status checking)
          // FIXED: Access playedMatchesResponse.data.playedMatches instead of playedMatchesResponse.data
          if (playedMatchesResponse.data && playedMatchesResponse.data.playedMatches && Array.isArray(playedMatchesResponse.data.playedMatches)) {
            fetchedPlayedMatches = playedMatchesResponse.data.playedMatches;
            setPlayedMatches(fetchedPlayedMatches);
            playedMatchIds = fetchedPlayedMatches.map((match: any) => match.matchid);
          } else {
            setPlayedMatches([]);
          }
        } catch (playedErr) {
          console.error('❌ DEBUG: Failed to fetch played matches:', playedErr);
          setPlayedMatches([]);
        }
        
        // Process upcoming matches and filter by selected season and hidden status
        if (upcomingMatchesResponse.data && Array.isArray(upcomingMatchesResponse.data)) {

          const upcomingMatchesForSeason = upcomingMatchesResponse.data.filter((match: Match) =>
            match.season === seasonToFetch && (isAdmin || !match.hidden) // Filter hidden matches for non-admins
          );


          allMatches = upcomingMatchesForSeason;
        }
        
        // Sort matches by date (earliest first)
        const sortedMatches = allMatches.sort((a: Match, b: Match) =>
          new Date(a.match_time).getTime() - new Date(b.match_time).getTime()
        );

        setMatches(sortedMatches);
        setFilteredMatches(sortedMatches);

        // Filter played matches to exclude hidden ones for non-admins
        const visiblePlayedMatches = fetchedPlayedMatches.filter((pm: PlayedMatch) => {
          // Find the corresponding match to check if it's hidden
          const matchData = allMatches.find(m => m.challonge_id === pm.matchid);
          // If we can't find the match data, it was filtered out as hidden - exclude it
          // If we find it, check if admin or not hidden
          return matchData && (isAdmin || !matchData.hidden);
        });

        // Notify parent component with loaded matches
        if (onMatchesLoaded) {
          onMatchesLoaded(sortedMatches, visiblePlayedMatches);
        }

      } catch (err) {
        console.error('Error fetching matches:', err);
        setError('Failed to load matches. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [selectedSeason]); // Removed onMatchesLoaded from dependency array to prevent infinite loop
  
  // Apply filters when filter criteria change
  useEffect(() => {
    if (!matches.length) return;
    
    let filtered = [...matches];
    
    // Apply match status filter
    if (matchStatusFilter !== 'all') {
      filtered = filtered.filter(match => {
        // A match is played ONLY if it exists in the played matches list
        const played = isMatchPlayed(match.challonge_id);
        return matchStatusFilter === 'played' ? played : !played;
      });
    }
    
    // Apply search filter (use external search term if provided, otherwise internal)
    const activeSearchQuery = searchTerm.trim() || searchQuery.trim();
    if (activeSearchQuery) {
      const query = activeSearchQuery.toLowerCase();
      filtered = filtered.filter(match => 
        match.team1.teamName.toLowerCase().includes(query) ||
        match.team2.teamName.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(a.match_time).getTime() - new Date(b.match_time).getTime();
        case 'playoff':
          if (a.isPlayoff !== b.isPlayoff) {
            return a.isPlayoff ? -1 : 1; // Playoff matches first
          }
          return new Date(a.match_time).getTime() - new Date(b.match_time).getTime();
        case 'alphabetical':
          return a.team1.teamName.localeCompare(b.team1.teamName);
        default:
          return 0;
      }
    });
    
    setFilteredMatches(filtered);
  }, [matchStatusFilter, searchQuery, sortBy, matches, playedMatches, searchTerm]);
  
  // Reset internal search (filters now controlled by parent)
  const resetSearch = () => {
    setSearchQuery('');
  };
  
  // Group matches for display based on view prop
  const playedMatchesList = filteredMatches.filter(match => {
    // A match is played ONLY if it exists in the played matches list
    const played = isMatchPlayed(match.challonge_id);
    return played;
  }).sort((a, b) =>
    // For played matches: Show LATEST first (descending order)
    new Date(b.match_time).getTime() - new Date(a.match_time).getTime()
  );

  const upcomingMatchesList = filteredMatches.filter(match => {
    // A match is upcoming if it's not played AND not currently live
    const played = isMatchPlayed(match.challonge_id);
    const live = isMatchLive(match);
    return !played && !live;
  }).sort((a, b) =>
    // For upcoming matches: Show EARLIEST first (ascending order)
    new Date(a.match_time).getTime() - new Date(b.match_time).getTime()
  );

  // Determine which list to show based on view prop
  const displayMatches = view === 'results' ? playedMatchesList : upcomingMatchesList;
  
  if (loading) {
    return (
      <div className="matches-loading">
        <div className="loading-spinner"></div>
        <h2>Loading matches...</h2>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="matches-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="btn btn-primary">Try Again</button>
      </div>
    );
  }
  
  return (
    <div className="match-page-overview">
      <div className="container">

        {/* Match Results */}
        <div className="matches-results">
          {displayMatches.length === 0 ? (
            <div className="no-matches">
              <h3>No {view === 'results' ? 'results' : 'upcoming matches'} found</h3>
              <p>Try changing your search criteria or check back later for matches.</p>
              <button className="btn btn-primary" onClick={resetSearch}>
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="match-list">
              <div className="match-rows">
                {displayMatches.map(match => {
                  const isPlayed = view === 'results';
                  const score = isPlayed ? getMatchScore(match) : null;

                  return (
                    <Link
                      key={match.challonge_id}
                      to={`/matches/${match.challonge_id}`}
                      className="match-row"
                    >
                      {/* Date column */}
                      <div className="match-row__date">
                        <div className="date-day">{formatDate(match.match_time)}</div>
                        <div className="date-time">{formatTime(match.match_time)}</div>
                      </div>

                      {/* Type/Group column */}
                      <div className="match-row__type">
                        <span className={`type-badge ${match.isPlayoff ? 'playoff' : 'group'}`}>
                          {match.isPlayoff ? 'Playoff' : `Group ${match.division}`}
                        </span>
                        <span className="format-badge">{getMatchFormat(match)}</span>
                      </div>

                      {/* Team 1 */}
                      <div className="match-row__team match-row__team--home">
                        <span className="team-name">{match.team1.teamName}</span>
                        <img
                          src={getTeamLogoUrl(match.team1)}
                          alt={match.team1.teamName}
                          className="team-logo"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = teamPlaceholderImage;
                          }}
                        />
                      </div>

                      {/* Score/VS column */}
                      <div className="match-row__score">
                        {isPlayed && score ? (
                          <span className="score">{score.team1Score} - {score.team2Score}</span>
                        ) : (
                          <span className="vs">VS</span>
                        )}
                        <span className="score-format">{getMatchFormat(match)}</span>
                      </div>

                      {/* Team 2 */}
                      <div className="match-row__team match-row__team--away">
                        <img
                          src={getTeamLogoUrl(match.team2)}
                          alt={match.team2.teamName}
                          className="team-logo"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = teamPlaceholderImage;
                          }}
                        />
                        <span className="team-name">{match.team2.teamName}</span>
                      </div>

                      {/* Stream indicator */}
                      {!isPlayed && match.stream_url && (
                        <div className="match-row__stream">
                          <FaTwitch />
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchPageOverview;